import React, { useEffect, useState } from 'react';
import config from '../config.js';

const API_BASE = config.API_BASE_URL;

/**
 * Lots vs stock for one material (jobq-api GET /alerts/lot_stock_check?material_id=, since 2026-10-20):
 * the same finding the 5 PM alert posts, shown where the material is edited. A full sheet's stock must
 * equal what its tracking numbers (MTN lots) say: sheets left on the open lots plus sheets scanned on
 * jobs not yet completed. Nothing shows when they agree, for a drop / remnant / shape (not lot-tracked
 * yet), or when the check can't be read (it must never get in the way of editing the material).
 */
export function lotStockMessages(check) {
  if (!check || !check.count) return [];
  const out = [];
  for (const r of check.mismatches || []) {
    if (r.no_open_lot) {
      out.push(`Stock ${r.stock}, but no open tracking number holds it.`);
    } else {
      const unposted = r.unposted_scans
        ? ` + ${r.unposted_scans} scanned but never taken off stock` : '';
      out.push(`Stock ${r.stock}, tracking numbers read ${r.derived} (${r.lot_remaining} left on the lots${unposted}).`);
    }
  }
  for (const r of check.ledger || []) {
    if (r.kind === 'stock_off_ledger') {
      out.push(r.last_ledger_qty == null
        ? `Stock ${r.stock} has no stock history at all (set without a ledger row).`
        : `Stock ${r.stock}, but its history ends at ${r.last_ledger_qty} (changed without a ledger row).`);
    } else {
      out.push(`Stock jumped ${r.previous_quantity_after} → ${r.quantity_before} with no ledger row.`);
    }
  }
  for (const r of check.lots_without_beacon || []) {
    out.push(`TRK ${String(r.mtn_id).padStart(6, '0')} has ${r.remaining} left and no beacon.`);
  }
  for (const r of check.unlotted_scans || []) {
    out.push(`Job #${r.master_job_num} cut ${r.scans} sheet(s) with no tracking number.`);
  }
  for (const r of check.finished_task_scans || []) {
    out.push(`TRK ${String(r.mtn_id).padStart(6, '0')}: ${r.scans} scan(s) on a ${String(r.status).toLowerCase()} task (cut, never taken off stock).`);
  }
  for (const r of check.unposted_completed_scans || []) {
    out.push(`Job #${r.master_job_num}: ${r.scans} sheet(s) recorded on the completed task but never taken off stock.`);
  }
  return out;
}

export default function LotStockBadge({ materialId, refreshKey }) {
  const [check, setCheck] = useState(null);

  useEffect(() => {
    let live = true;
    setCheck(null);
    if (!materialId) return undefined;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/alerts/lot_stock_check?material_id=${materialId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (live) setCheck(data);
      } catch (_) {
        // the badge is advisory: a failed read shows nothing
      }
    })();
    return () => { live = false; };
  }, [materialId, refreshKey]);

  const messages = lotStockMessages(check);
  if (!messages.length) return null;
  return (
    <div className="alert alert-warning py-2 mb-3" role="alert" data-testid="lot-stock-badge">
      <div className="fw-bold">Stock and tracking numbers disagree</div>
      <ul className="mb-1 ps-3">
        {messages.map((m, i) => <li key={i}>{m}</li>)}
      </ul>
      <div className="small text-muted">
        Fix the count and the tracking number together (receive, adjust with a reason, or correct the
        lot), never just one.
      </div>
    </div>
  );
}
